/* eslint-disable @next/next/next-script-for-ga */
import { createTheme } from "@mui/material/styles"
import { AppProps as NextAppProps } from "next/app"
import { Geologica } from "next/font/google"
import Head from "next/head"
import React, { FC, useEffect, useState } from "react"

import { Fonts } from "src/components/core/fonts"
import { Providers } from "src/components/core/providers"
import LocalStorageSync from "src/helpers/LocalStorageSync"
import { setLocale, useLocale } from "src/states/locale"
import { setStateVersion, useStateVersion } from "src/states/version"
import { DEFAULT_LOCALE, isLocale, Locale } from "src/translations"
import { SharedPageProps } from "src/types/SharedPageProps"
import "what-input"
import "../assets/icons/_fontawesome.css"
import "../styles/wordpress/styles.css"
import "./_app.scss"
// import LocalStorageCleanup from "../helpers/localStorageCleanup"

type AppProps = NextAppProps<SharedPageProps>
const GeologicaFont = Geologica({ subsets: ["latin"], display: "swap" })

const App: FC<AppProps> = ({ Component: Page, pageProps }) => {
  const [hasMounted, setHasMounted] = useState(false)
  const clientStateVersion = useStateVersion()
  const { messages, generalSettings, page, typography, color } =
    pageProps as SharedPageProps
  const meta = page?.data?.meta
  const locale = useLocale()

  useEffect(() => {
    if (!hasMounted) {
      setHasMounted(true)
    }
    let navigatorLocale = navigator?.language?.split("-")?.[0] as Locale
    let windowLocale = window?.location?.pathname?.split("/")?.[1] as Locale

    if (isLocale(windowLocale)) {
      setLocale(windowLocale)
    } else if (!locale) {
      setLocale(navigatorLocale ?? DEFAULT_LOCALE)
    }

    // This environment variable is set to the latest commit ID in the build pipeline.
    if (clientStateVersion !== process.env.APP_STATE_VERSION!) {
      console.debug("Clearing local storage...")

      localStorage.clear()
      setStateVersion(process.env.APP_STATE_VERSION!)
    }
  }, [])

  if (!hasMounted) {
    return null
  }

  let title
  if (generalSettings?.data?.themeOptions?.generals?.subtitle) {
    title = `${generalSettings?.data?.themeOptions?.generals?.title} - ${generalSettings?.data?.themeOptions?.generals?.subtitle}`
  } else {
    title = generalSettings?.data?.themeOptions?.generals?.title
  }

  const theme = createTheme({
    typography: {
      button: {
        textTransform: "none",
      },
      fontFamily:
        // typography?.data?.themeOptions?.typography?.body?.fontFamily ??
        "Geologica",
    },
    palette: {
      primary: {
        main: color?.data?.themeOptions?.color?.primary ?? "#3F54B5",
      },
      secondary: {
        main: color?.data?.themeOptions?.color?.secondary ?? "#3E9EBB",
      },
      info: {
        main: "#ccc",
      },
    },
    breakpoints: {
      values: {
        xs: 0,
        sm: 480,
        md: 480,
        lg: 960,
        xl: 1280,
      },
    },
  })

  return (
    <Providers messages={messages} theme={theme}>
      <Head>
        <link
          href="https://fonts.googleapis.com/css2?family=Geologica:wght@400&display=swap"
          rel="stylesheet"
        />
        <link href="https://app.usercentrics.eu" rel="preconnect" />
        <link href="https://api.usercentrics.eu" rel="preconnect" />
        <link
          as="script"
          href="https://app.usercentrics.eu/browser-ui/latest/loader.js"
          rel="preload"
        />
        <title>
          {title} - {page?.data?.title}
        </title>
        <meta content={meta?.description ?? ""} name="description" />
        <meta content={meta?.keywords ?? ""} name="keywords" />
        <link
          href={generalSettings?.data?.themeOptions?.generals?.favicon ?? ""}
          rel="shortcut icon"
        />
        <script
          async
          data-settings-id={`${process.env.USER_CENTRICS_ID}`}
          id="usercentrics-cmp"
          src="https://app.usercentrics.eu/browser-ui/latest/loader.js"
        ></script>
        {process.env.GTM_ID && (
          <script
            data-usercentrics="Google Tag Manager"
            type="text/plain"
            dangerouslySetInnerHTML={{
              __html: `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','${process.env.GTM_ID}');`,
            }}
          />
        )}
      </Head>
      <Fonts />
      <LocalStorageSync />
      <Page {...pageProps} />
    </Providers>
  )
}

export default App
